import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Progress,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiClock,
} from 'react-icons/fi';
import { Question } from '@grcs/views/shared/components/editors/QuestionEditor';
import { parseQuestions } from '@grcs/utils/content/parseQuestions';
import { Empty } from '../ui/Empty';
import { MultipleChoice } from './MultipleChoice';

const QUESTION_TIME_SECONDS = 15 * 60;

export interface IDoQuestionProps {
  value: string | undefined;
  timed?: boolean;
  submitted?: boolean;
  onRetry: () => void;
  onResult: (questionIndex: number, answer: boolean) => void;
}

export const DoQuestions: React.FC<IDoQuestionProps> = ({
  value,
  timed = true,
  submitted = false,
  onRetry,
  onResult,
}) => {
  const questions: Question[] = useMemo(
    () => (value ? parseQuestions(value) : []),
    [value],
  );
  const [selections, setSelections] = useState<Record<number, number>>({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(QUESTION_TIME_SECONDS);
  const isLastMinute = timeLeft > 0 && timeLeft <= 60;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const border = useColorModeValue('gray.200', 'gray.600');
  const surface = useColorModeValue('gray.50', 'gray.800');
  const muted = useColorModeValue('gray.600', 'gray.300');
  const completeBg = useColorModeValue('green.50', 'green.900');

  useEffect(() => {
    if (!timed || submitted) return;
    const timer = window.setInterval(
      () => setTimeLeft((previous) => Math.max(0, previous - 1)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [timed, submitted]);

  const answeredCount = Object.keys(selections).length;
  const total = questions.length;
  const allAnswered = total > 0 && answeredCount === total;
  const question = questions[activeIndex];
  const goToQuestion = (index: number) => {
    setActiveIndex(index);
    headingRef.current?.focus();
    headingRef.current?.scrollIntoView?.({ block: 'center' });
  };
  const restart = () => {
    setSelections({});
    setTimeLeft(QUESTION_TIME_SECONDS);
    onRetry();
    goToQuestion(0);
  };

  if (!question?.question || !question.choices?.length)
    return <Empty>暂无题目</Empty>;

  if (submitted) {
    const correctCount = questions.filter(
      (item, index) => item.answer?.[selections[index]] === true,
    ).length;
    const score = Math.round((correctCount / total) * 100);

    return (
      <Stack spacing={4}>
        <Box role="status" bg={completeBg} p={4} borderRadius="xl">
          <Heading size="md">
            答对 {correctCount} / {total} 题
          </Heading>
        </Box>
        {questions.map((item, index) => {
          const selected = selections[index];
          const correct = item.answer?.[selected] === true;
          return (
            <Box key={index} borderWidth="1px" borderRadius="xl" p={4}>
              <Heading size="sm" mb={2}>
                {index + 1}. {item.question}
              </Heading>
              <Badge colorScheme={correct ? 'green' : 'red'}>
                {correct ? '正确' : '错误'}
              </Badge>
              <Text mt={2}>
                你的答案：
                {selected === undefined
                  ? '未作答'
                  : `${String.fromCharCode(65 + selected)}. ${
                      item.choices[selected]
                    }`}
              </Text>
              <Text fontWeight="semibold" mt={1}>
                正确答案：
                {item.choices
                  .map((choice, choiceIndex) =>
                    item.answer?.[choiceIndex]
                      ? `${String.fromCharCode(65 + choiceIndex)}. ${choice}`
                      : null,
                  )
                  .filter(Boolean)
                  .join('；')}
              </Text>
            </Box>
          );
        })}
      </Stack>
    );
  }

  return (
    <>
      <Box display="none" sx={{ '@media print': { display: 'block' } }}>
        {questions.map((item, index) => (
          <Box key={index} mb={3} sx={{ breakInside: 'avoid' }}>
            <Text fontWeight="bold" mb={1} lineHeight="1.4">
              {index + 1}. {item.question}
            </Text>
            {item.choices.map((choice, choiceIndex) => (
              <Text key={choiceIndex} mb={0.5} lineHeight="1.4">
                {String.fromCharCode(65 + choiceIndex)}. {choice}
              </Text>
            ))}
          </Box>
        ))}
      </Box>
      <Stack spacing={3} sx={{ '@media print': { display: 'none' } }}>
        <Box
          className="do-questions-progress"
          display={timed ? 'block' : 'none'}
          px={3}
          py={2}
          bg={surface}
          borderWidth="1px"
          borderColor={border}
          borderRadius="xl"
        >
          <HStack
            justify="space-between"
            align="start"
            spacing={3}
            flexWrap="wrap"
          >
            {timed && (
              <HStack fontSize="sm" w="full" spacing={3}>
                <FiClock aria-hidden />
                <Text
                  whiteSpace="nowrap"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  剩余 {Math.floor(timeLeft / 60)}:
                  {String(timeLeft % 60).padStart(2, '0')}
                </Text>
                <Progress
                  aria-label="剩余答题时间"
                  aria-valuetext={`剩余 ${Math.floor(timeLeft / 60)} 分 ${
                    timeLeft % 60
                  } 秒`}
                  value={(timeLeft / QUESTION_TIME_SECONDS) * 100}
                  flex="1"
                  minW="80px"
                  height="8px"
                  borderRadius="full"
                  colorScheme={timeLeft <= 60 ? 'red' : 'blue'}
                  sx={{
                    '@keyframes remainingTimePulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.3 },
                    },
                    '& > [role="progressbar"]': {
                      animation: isLastMinute
                        ? 'remainingTimePulse 1s ease-in-out infinite'
                        : 'none',
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                      '& > [role="progressbar"]': { animation: 'none' },
                    },
                  }}
                />
              </HStack>
            )}
          </HStack>
          {timed && timeLeft === 0 && (
            <Text role="status" mt={3}>
              答题时间已到，请检查答案并提交作业。
            </Text>
          )}
        </Box>

        <Box
          borderWidth="1px"
          borderColor={border}
          borderRadius="xl"
          p={{ base: 3, md: 4 }}
        >
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" color={muted}>
              第 {activeIndex + 1} / {total} 题 · 单选
            </Text>
            <Badge
              colorScheme={
                selections[activeIndex] === undefined ? 'gray' : 'blue'
              }
            >
              {selections[activeIndex] === undefined ? '请选择' : '已选择'}
            </Badge>
          </HStack>
          <Heading
            ref={headingRef}
            tabIndex={-1}
            size="sm"
            lineHeight="1.0"
            mb={3}
            whiteSpace="pre-wrap"
          >
            {question.question}
          </Heading>
          <MultipleChoice
            question={question.question}
            choices={question.choices}
            selectedIndex={selections[activeIndex]}
            onSelect={(selected) => {
              setSelections((previous) => ({
                ...previous,
                [activeIndex]: selected,
              }));
              onResult(activeIndex, question.answer?.[selected] === true);
            }}
          />
          <HStack
            justify="space-between"
            mt={3}
            pt={2}
            borderTopWidth="1px"
            borderColor={border}
          >
            <Button
              variant="ghost"
              leftIcon={<FiArrowLeft />}
              isDisabled={activeIndex === 0}
              onClick={() => goToQuestion(activeIndex - 1)}
            >
              上一题
            </Button>
            {activeIndex < total - 1 ? (
              <Button
                colorScheme="blue"
                rightIcon={<FiArrowRight />}
                onClick={() => goToQuestion(activeIndex + 1)}
              >
                下一题
              </Button>
            ) : !allAnswered ? (
              <Button
                colorScheme="blue"
                onClick={() =>
                  goToQuestion(
                    questions.findIndex(
                      (_, index) => selections[index] === undefined,
                    ),
                  )
                }
              >
                继续未答题
              </Button>
            ) : (
              <Button variant="outline" onClick={() => goToQuestion(0)}>
                检查答案
              </Button>
            )}
          </HStack>
        </Box>
      </Stack>
    </>
  );
};
